
  import {defineComponent, ref, Ref, watch, nextTick, onMounted, onBeforeUnmount} from "vue";
  import { useStore } from "vuex";
  import {ApiGetters, FormActions, FormGetters} from './../../Store/index';
  import { isEmpty, numberFormat } from './../../Composables/functions';
  import {navigateToStepWithRouterLink, pushFunnelStepToMatomo, checkGoogleMapsConsent, triggerMapSearch} from './../../Composables/form';
  import {valuateService, getImagesForVehicles, sendCdpApi, isLoadingAnimationEnabled} from './../../Composables/api';
  import { ResultApi } from './../../Store/api/types';
  import './../../Store/types';
  import { ElIcon } from "element-plus";
  import InfoIcon from "./../Icons/Info.vue";

  import SelectComponent from "./../Form/SelectComponent.vue";
  import ModalComponent from "./../Form/ModalComponent.vue";

  import Vue3Odometer from 'vue3-odometer';

  export default defineComponent({
    name: "ResultComponent",

    components: {
      ModalComponent,
      SelectComponent,
      Vue3Odometer,
      InfoIcon,
      ElIcon
    },

    props: {
      isShowed: {
        type: Boolean,
        default: false
      }
    },

    setup(props) {
      // Get the Vuex store instance
      const store = useStore();

      const defaultResultApi: ResultApi = {
        manufacturerName: '',
        mainTypeNameWithoutYear: '',
        imageBase64Encoded: '',
        imageFetched: false,
        buildYearRange: '',
        containerName: '',
        subTypeName: '',
        PurchasePriceGross: 0,
        error: '',
        Container: '',
        DatECodeFormatted: '',
        ConstructionTime: '',
        equipments: {},
        equipmentsSuper: {},
      };

      // Get various data from the store using getters
      const icons = store.getters[`form/${FormGetters.GET_ICONS}`],
          labels = store.getters[`form/${FormGetters.GET_LABELS}`],
          urls = store.getters[`form/${FormGetters.GET_URLS}`],
          settings = store.getters[`form/${FormGetters.GET_SETTINGS}`],
          googleMapsConsent: Ref<boolean> = ref(false);

      let selectedCar: Ref<ResultApi> = ref(defaultResultApi),
          result: Ref<ResultApi> = ref(defaultResultApi),
          showResultPage = ref(false),
          formHasContentElements = ref(false),
          vehicleModalBody = ref(''),
          wrapper = ref<HTMLElement | null>(null),
          isShowedValue = ref(false),
          odometerPriceValue = ref<Number>(0),
          noResultAvailable = ref(false),
          observer = ref<MutationObserver | null>(null);

      // Watch for changes in isCar
      watch(
          [
              () => store.getters[`form/${FormGetters.GET_DATA}`].selectedCar.value,
            () => store.getters[`form/${FormGetters.GET_DATA}`].hasContentElements.value,
            () => store.getters[`form/${FormGetters.GET_DATA}`].hiddenContentQuerySelector.value,
            () => store.getters[`form/${FormGetters.GET_SHOW_RESULT_PAGE}`]
        ],
        async ([newSelectedCar, newFormHasContentElements, hiddenContentQuerySelector, newShowResultPage]) => {

            // Get the selected car from the store
            selectedCar.value = newSelectedCar;
            formHasContentElements.value = newFormHasContentElements;
            showResultPage.value = newShowResultPage;

            // Check if selectedCar is not empty
            if (!isEmpty(selectedCar.value)) {
              // Fetch new image for car
              if (!('imageFetched' in selectedCar.value)) {
                await getImagesForVehicles(store);
              }

              // Call the valuateService function to fetch service result
              await valuateService(store);

              // Scroll to the element
              if (wrapper.value && showResultPage.value) {
                  // Calculate the target scroll position with a 100-pixel offset
                  waitForScrollConditionsAndScroll(wrapper.value, 100);
              }

              await nextTick(() => {
                // Show content element
                if (showResultPage.value) {
                  if (formHasContentElements.value) {
                    const contentElementsDiv = document.querySelector('.short-eval-contentelements');
                    if (contentElementsDiv) {
                      contentElementsDiv.classList.remove('hidden');
                    }
                  }
                    if (hiddenContentQuerySelector != '') {
                        const elements = document.querySelectorAll(hiddenContentQuerySelector);

                        // Add the "hidden" class to each element
                        elements.forEach(element => {
                            element.classList.add('hidden');
                        });
                    }
                  // @ts-ignore
                  window._mtm.push({
                    'event': 'fgw_overlay_shown',
                    'fgw_overlay_shown': {
                      'properties': {
                        'timestamp': (new Date().toISOString()),
                        'url': window.location.href,
                        'overlay_type': "FGW Overlay",
                        'overlay_trigger': "extensive usage of the FGW funnel within a session"
                      }
                    }
                  });

                  // add funnelStep to Matomo
                  pushFunnelStepToMatomo(store);
                }

                result.value = store.getters[`form/${FormGetters.GET_DATA}`].valuateServiceResult.value;
              });

              // Retrieve necessary values from the store
              const tmpSelectCarValue = selectedCar.value;

              // Extract the firstRegistration date and format it
              const formattedDate = store.getters[`form/${FormGetters.GET_INPUTS}`].firstRegistration.value;

              // Initialize modalContent with basic information
              let modalContent = `
                <strong>${tmpSelectCarValue.manufacturerName} ${tmpSelectCarValue.mainTypeNameWithoutYear} ${tmpSelectCarValue.subTypeName}, ${tmpSelectCarValue.buildYearRange}</strong><br/>
                ${tmpSelectCarValue.containerName}<br/>
              `;

              // Check if there are equipments and add them to modalContent
              if (tmpSelectCarValue.equipments && Object.keys(tmpSelectCarValue.equipments).length > 0) {
                Object.values(tmpSelectCarValue.equipments).forEach((value) => {
                  modalContent += `${value}<br/>`;
                });
              }

              // Check if there are super equipments and add them to modalContent
              if (tmpSelectCarValue.equipmentsSuper && Object.keys(tmpSelectCarValue.equipmentsSuper).length > 0) {
                Object.values(tmpSelectCarValue.equipmentsSuper).forEach((value) => {
                  modalContent += `${value}<br/>`;
                });
              }

              // Add additional information to modalContent
              modalContent += ` <br/>
                Laufleistung: <strong>${numberFormat(store.getters[`form/${FormGetters.GET_INPUTS}`].mileage.value,0,0,true)} km</strong> <br/>
                Erstzulassung: <strong>${formattedDate}</strong> <br/>
              `;

              // Check if DatECodeFormatted is available and add it to modalContent
              if (result.value.DatECodeFormatted) {
                modalContent += `
                  DAT €uropa-Code®: <strong>${result.value.DatECodeFormatted} ${result.value.Container} ${result.value.ConstructionTime}</strong>
                `;
              }

              // Update the vehicleModalBody with the final modalContent
              vehicleModalBody.value = modalContent;

              // Send CDP Api data
              sendCdpApi(store, modalContent);
            }

            // Update isShowedValue
            isShowedValue.value = props.isShowed;

            // If isShowed is false, reset the odometer value to 0; otherwise, set it to PurchasePriceGross
            odometerPriceValue.value = isShowedValue.value ? result.value.PurchasePriceGross : 0;

            noResultAvailable.value = !Object.keys(result.value).length ||
                result.value.hasOwnProperty('error') || !result.value.PurchasePriceGross;
          },
          { immediate: true } // Trigger the watch callback immediately
      );

      // zip code input validation
      watch(
          () => googleMapsConsent.value,
          (consentNow, consentBefore) => {
              if (!consentBefore && consentNow && showResultPage.value) {
                  setTimeout(() => {
                      checkGoogleMapsConsent().then((canUseMaps) => {
                          if (canUseMaps) {
                              const zip = store.getters[`form/${FormGetters.GET_INPUTS}`].fieldLocation;
                              triggerMapSearch(zip.value);
                          } else {
                              console.warn('Google Maps not available despite consent.');
                          }
                      });
                  }, 500);
              }
          }
      );

      onMounted(async () => {
          googleMapsConsent.value = await checkGoogleMapsConsent();

          const observer = new MutationObserver(() => {
              checkGoogleMapsConsent().then((currentConsent) => {
                  if (!googleMapsConsent.value && currentConsent) {
                      googleMapsConsent.value = true;
                  }
              });
          });

          observer.observe(document.documentElement, {
              childList: true,
              subtree: true,
          });
      });

      onBeforeUnmount(() => {
        observer?.value?.disconnect();
      });

      const hideResultPage = async () => {
        await navigateToStepWithRouterLink(store, 1, 'start');
        await store.dispatch(`form/${FormActions.SET_SHOW_RESULT_PAGE}`, false);
        await store.dispatch(`form/${FormActions.SET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`, false);

          const hasContentElements = store.getters[`form/${FormGetters.GET_DATA}`].hasContentElements.value;
          const hiddenContentQuerySelector = store.getters[`form/${FormGetters.GET_DATA}`].hiddenContentQuerySelector.value;
            if (hasContentElements) {
                const contentElementsDiv = document.querySelector('.short-eval-contentelements');
                if (contentElementsDiv) {
                contentElementsDiv.classList.add('hidden');
                }
            }
            if(hiddenContentQuerySelector != '') {
                const elements = document.querySelectorAll(hiddenContentQuerySelector);

                // Add the "hidden" class to each element
                elements.forEach(element => {
                    element.classList.remove('hidden');
                });
            }
      };

      const scrollToMap = () => {
        const mapElement = document.querySelector('#bh-sl-map-container');

        if (mapElement) {
          mapElement.scrollIntoView({behavior: 'smooth'});
        }
      };

      const getUrlNoPrice = () => {
        return urls.expertPartnerUrl+'?sg='+store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties.zipCode;
      };

      const waitForScrollConditionsAndScroll = (refEl: HTMLElement, offset = 0, maxTries = 20, interval = 100) => {
            let tries = 0;

            const checkAndScroll = () => {
                const ongoing = store.getters[`api/${ApiGetters.GET_ON_GOING_REQUEST}`];
                const loading = isLoadingAnimationEnabled();

                if (ongoing === 0 && !loading) {
                    const targetScrollPosition = refEl.getBoundingClientRect().top + window.scrollY - offset;

                    window.scrollTo({
                        top: targetScrollPosition,
                        behavior: 'smooth',
                    });
                } else if (tries < maxTries) {
                    tries++;
                    setTimeout(checkAndScroll, interval);
                }
            };

            checkAndScroll();
        }

      // Return the data
      return {
        labels,
        icons,
        selectedCar,
        result,
        vehicleModalBody,
        hideResultPage,
        settings,
        wrapper,
        isShowedValue,
        noResultAvailable,
        odometerPriceValue,
        scrollToMap,
        getUrlNoPrice,
        store
      }
    }
  })
